import type { MailMessageView } from "@kwp/contracts";
import { useEffect, useMemo, useRef, useState } from "react";
import { cx } from "../../ui";
import { Attachments } from "./attachments";
import { formatRecipient } from "./compose-model";
import { useApiOrigin } from "../../lib/clients";
import { foldQuotedHtml, foldQuotedText } from "./quoted";
import { buildSrcdoc, IFRAME_SANDBOX, reportedHeight } from "./srcdoc";

const FULL_DATE = new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" });

/**
 * One message of a thread as a card (architecture §1, replies "A"): folded, one line (initial,
 * name, first words, date); open, a header (sender, address, recipients, time) and the body.
 * Messages the mailbox sent read "You" on a tinted card. HTML renders in the sandboxed frame (see
 * `srcdoc.ts`), as tall as the email (the frame reports its height), so the page scrolls, not
 * the frame. A message with only text renders as text.
 */
export function MessageCard({
  message,
  expanded,
  onToggle,
  earlierTexts = [],
}: {
  message: MailMessageView;
  expanded: boolean;
  onToggle: () => void;
  /** Plain text of the thread's earlier messages, to find where this one quotes them. */
  earlierTexts?: readonly string[];
}) {
  const sender = message.from.name === null ? message.from.email : message.from.name;
  const own = message.from.email.toLowerCase() === message.accountAddress.toLowerCase();
  const who = own ? "You" : sender;
  return (
    <article aria-label={`Message from ${sender}`} className={cx("min-w-0 rounded-lg border", own ? "border-accent/25 bg-accent/5" : "border-border bg-surface")}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full min-w-0 items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm hover:bg-surface-2/60"
      >
        <span
          aria-hidden="true"
          className={cx("grid size-7 shrink-0 place-items-center rounded-full text-xs font-semibold", own ? "bg-accent text-accent-contrast" : "bg-surface-2 text-text")}
        >
          {who.trim().charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 truncate font-semibold text-text">{who}</span>
        {/* The address shows on wider screens; a phone has no room beside the initial and the date. */}
        {expanded && message.from.name !== null ? <span className="hidden shrink-0 text-xs text-muted sm:inline">{message.from.email}</span> : null}
        <span className="min-w-0 flex-1 truncate text-xs text-muted">
          {expanded
            ? `→ ${message.to.map(formatRecipient).join(", ")}${message.cc.length > 0 ? `, cc ${message.cc.map(formatRecipient).join(", ")}` : ""}`
            : message.snippet}
        </span>
        <time dateTime={message.date.toISOString()} className="shrink-0 text-xs text-muted">
          {FULL_DATE.format(message.date)}
        </time>
      </button>
      {expanded ? (
        <div className="flex flex-col gap-2 px-3 pb-3">
          <MessageBody message={message} earlierTexts={earlierTexts} />
          <Attachments messageId={message.id} attachments={message.attachments} />
        </div>
      ) : null}
    </article>
  );
}

/**
 * The body without the quoted history (`quoted.ts`): earlier messages are cards of their own, so
 * only what is new shows, with "Show quoted text" for the rest.
 */
function MessageBody({ message, earlierTexts }: { message: MailMessageView; earlierTexts: readonly string[] }) {
  const sender = message.from.email;
  const imageOrigin = useApiOrigin();
  const [showQuoted, setShowQuoted] = useState(false);
  const html = message.html;
  const text = message.text;
  // Keyed on the texts themselves: the thread view builds the array anew on each render.
  const earlierKey = earlierTexts.join("\u0000");
  const folded = useMemo(() => {
    const earlier = earlierKey === "" ? [] : earlierKey.split("\u0000");
    if (html !== null) return foldQuotedHtml(html, earlier);
    return text !== null ? foldQuotedText(text) : null;
  }, [html, text, earlierKey]);

  const shown = folded === null ? null : showQuoted ? (html ?? text) : folded.latest;
  const srcdoc = useMemo(() => (html === null || shown === null ? null : buildSrcdoc(shown, { imageOrigin })), [html, shown, imageOrigin]);
  const toggle =
    folded !== null && folded.folded ? (
      <button type="button" onClick={() => setShowQuoted((v) => !v)} className="self-start rounded-md bg-surface-2 px-2 py-0.5 text-xs text-muted hover:text-text">
        {showQuoted ? "Hide quoted text" : "••• Show quoted text"}
      </button>
    ) : null;

  if (srcdoc === null) {
    return (
      <>
        <pre className="whitespace-pre-wrap break-words font-sans text-sm text-text">{shown === null ? "(This message has no text.)" : shown}</pre>
        {toggle}
      </>
    );
  }
  return (
    <>
      <EmailFrame key={showQuoted ? "all" : "latest"} title={`Message from ${sender}`} srcdoc={srcdoc} />
      {toggle}
    </>
  );
}

/** Until the frame reports its height. */
const INITIAL_HEIGHT = 320;

/**
 * The email's frame, as tall as the email. Only a message from this frame's own window counts
 * (its origin is opaque, so the window is the check), and the height is bounded (`reportedHeight`).
 */
function EmailFrame({ title, srcdoc }: { title: string; srcdoc: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(INITIAL_HEIGHT);
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (ref.current === null || e.source !== ref.current.contentWindow) return;
      const h = reportedHeight(e.data);
      if (h !== null) setHeight(Math.max(h, 40));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
  return (
    <iframe
      ref={ref}
      title={title}
      sandbox={IFRAME_SANDBOX}
      srcDoc={srcdoc}
      referrerPolicy="no-referrer"
      scrolling="no"
      style={{ height }}
      className="block w-full bg-white"
    />
  );
}
